import { useState } from "react";
import Link from "next/link";
import { useI18n } from "@/i18n";
import { UserProfileMenu } from "./UserProfileMenu";
import { useAuthContext } from "@/providers/AuthProvider";

interface NavItem {
  id: string;
  icon: React.ReactNode;
  labelKey: keyof typeof import("@/i18n/locales").locales.en.nav;
  href: string;
}

// Icons - 更細緻的線條
const HomeIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);

const DashboardIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="9" rx="1" />
    <rect x="14" y="3" width="7" height="5" rx="1" />
    <rect x="14" y="12" width="7" height="9" rx="1" />
    <rect x="3" y="16" width="7" height="5" rx="1" />
  </svg>
);

const PlusIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="16" />
    <line x1="8" y1="12" x2="16" y2="12" />
  </svg>
);

const HelpIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const FileIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
  </svg>
);

const InfoIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

const ProfileIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const BotIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 8V4H8" />
    <rect width="16" height="12" x="4" y="8" rx="2" />
    <path d="M2 14h2" />
    <path d="M20 14h2" />
    <path d="M15 13v2" />
    <path d="M9 13v2" />
  </svg>
);

// 未登入時的導航項目
const NAV_ITEMS_GUEST: NavItem[] = [
  { id: "home", icon: <HomeIcon />, labelKey: "home", href: "/" },
  { id: "create", icon: <PlusIcon />, labelKey: "create", href: "/seller" },
  { id: "skills", icon: <BotIcon />, labelKey: "skills", href: "/skills" },
  { id: "support", icon: <HelpIcon />, labelKey: "support", href: "/support" },
  { id: "terms", icon: <FileIcon />, labelKey: "terms", href: "/terms" },
];

// 登入後的導航項目（桌面版）
const NAV_ITEMS_AUTH: NavItem[] = [
  { id: "home", icon: <HomeIcon />, labelKey: "home", href: "/" },
  { id: "dashboard", icon: <DashboardIcon />, labelKey: "dashboard", href: "/dashboard" },
  { id: "create", icon: <PlusIcon />, labelKey: "create", href: "/seller" },
  { id: "skills", icon: <BotIcon />, labelKey: "skills", href: "/skills" },
  { id: "support", icon: <HelpIcon />, labelKey: "support", href: "/support" },
  { id: "terms", icon: <FileIcon />, labelKey: "terms", href: "/terms" },
];

// 登入後的行動版導航項目
const NAV_ITEMS_AUTH_MOBILE: NavItem[] = [
  { id: "home", icon: <HomeIcon />, labelKey: "home", href: "/" },
  { id: "dashboard", icon: <DashboardIcon />, labelKey: "dashboard", href: "/dashboard" },
  { id: "create", icon: <PlusIcon />, labelKey: "create", href: "/seller" },
  { id: "profile", icon: <ProfileIcon />, labelKey: "profile", href: "/dashboard/settings" },
];

export function SideNav() {
  const { t } = useI18n();
  const { isAuthenticated } = useAuthContext();
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  // 根據登入狀態選擇導航項目
  const desktopNavItems = isAuthenticated ? NAV_ITEMS_AUTH : NAV_ITEMS_GUEST;
  const mobileNavItems = isAuthenticated ? NAV_ITEMS_AUTH_MOBILE : NAV_ITEMS_GUEST.slice(0, 4);

  return (
    <>
      {/* Desktop Side Nav */}
      <nav className="side-nav">
        <div className="side-nav-glass">
          {/* User Profile (shown at top when logged in) */}
          {isAuthenticated && (
            <div className="side-nav-profile">
              <UserProfileMenu />
            </div>
          )}
          {desktopNavItems.map((item) => (
            <Link
              key={item.id}
              href={item.href}
              className={`side-nav-item ${hoveredId === item.id ? "hovered" : ""}`}
              onMouseEnter={() => setHoveredId(item.id)}
              onMouseLeave={() => setHoveredId(null)}
            >
              <span className="side-nav-icon">{item.icon}</span>
              {hoveredId === item.id && (
                <span className="side-nav-tooltip">{t.nav[item.labelKey]}</span>
              )}
            </Link>
          ))}
        </div>
      </nav>

      {/* Mobile Bottom Nav */}
      <nav className="bottom-nav">
        <div className="bottom-nav-glass">
          {mobileNavItems.map((item) => (
            <Link key={item.id} href={item.href} className="bottom-nav-item">
              <span className="bottom-nav-icon">{item.icon}</span>
              <span className="bottom-nav-label">{t.nav[item.labelKey]}</span>
            </Link>
          ))}
        </div>
      </nav>
    </>
  );
}
